import _ from "lodash";
import { DateTime } from "luxon";

import { simpleFormatNumberEquity, stripNonNumericCharacters } from "lib/frontend/cm/utils/helpers";
import { getBonusTargetInputMode } from "lib/shared/proposals";
import { VariableInputMode } from "prisma/cm/client";

import { FieldValueResolver } from "./FieldValueResolver";
import { DefinitionFieldType, Mixin, FieldFormatterFunc, FieldProposal } from "./types";
import {
  decimalsForProposal,
  decimalsForRangeNominal,
  decimalsForUserNominal,
} from "../compensation";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { getCurrencyOffInstance, treatLocalCurrencyAs } from "../currency/getCurrencyField";

const DateFilterParams = {
  valueFormatter: (params: any) => {
    return params.value ? new Date(params.value).toLocaleDateString() : "";
  },
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    if (!cellValue) return -1;
    const cellDate = DateTime.fromISO(cellValue).toJSDate();
    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0;
    }
    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    }
    if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }
  },
  browserDatePicker: true,
};

export const typeMixins: Record<DefinitionFieldType, Mixin> = {
  DOLLAR: {
    formatter: ({ value, instance, context }) => {
      const sourceCurrency = getCurrencyOffInstance(instance);

      if (_.isNil(value)) {
        return "";
      }

      if (context.exchangeRates) {
        const targetCurrency = treatLocalCurrencyAs(context.targetCurrency, sourceCurrency);
        if (!targetCurrency || !sourceCurrency) {
          return value;
        }

        let decimals: number = 0;

        if (FieldValueResolver.validateAsProposal(instance)) {
          decimals = decimalsForProposal({ proposal: instance, company: context?.company });
        } else if (FieldValueResolver.validateAsUser(instance)) {
          decimals = decimalsForUserNominal({ user: instance, company: context?.company });
        } else if (FieldValueResolver.validateAsRange(instance)) {
          //@ts-expect-error TS2322
          decimals = decimalsForRangeNominal({ range: instance, company: context?.company });
        }

        return getConvertedCurrency({
          value,
          options: {
            sourceCurrency: sourceCurrency,
            targetCurrency: targetCurrency,
            exchangeRates: context.exchangeRates,
            decimals,
          },
        }).formatted;
      } else {
        return value;
      }
    },
    comparator: (a, b, instanceA, instanceB, ctx) => {
      const exchangeRates = ctx.exchangeRates;
      if (!exchangeRates) {
        throw new Error("Missing exchange rates for nominal field comparator");
      }

      const usageModelA = FieldValueResolver.getUsageModel(instanceA);
      const usageModelB = FieldValueResolver.getUsageModel(instanceB);

      if (_.isNil(a) && _.isNil(b)) return 0;
      if (_.isNil(a)) return 1;
      if (_.isNil(b)) return -1;

      if (!usageModelA || !usageModelB) {
        return 0;
      }

      const currencyA = getCurrencyOffInstance(instanceA);
      const currencyB = getCurrencyOffInstance(instanceB);

      const usdA = getConvertedCurrency({
        value: a,
        options: {
          sourceCurrency: currencyA,
          targetCurrency: "USD",
          exchangeRates: exchangeRates,
        },
      }).raw;

      const usdB = getConvertedCurrency({
        value: b,
        options: {
          sourceCurrency: currencyB,
          targetCurrency: "USD",
          exchangeRates: exchangeRates,
        },
      }).raw;

      return usdA - usdB;
    },
    agGridSettings: {
      cellExportFormatter: (params) =>
        stripNonNumericCharacters(params.formattedValue ?? params.value),
    },
  },
  PERCENT: {
    formatter: ({ value }) => (!_.isNil(value) ? `${(value * 100).toFixed(2)}%` : null),
  },
  TEXT: {},
  NUMBER: {
    formatter: ({ value }) => {
      return value?.toLocaleString();
    },
  },
  DATE: {
    formatter: ({ value, context }) => {
      const company = context.company;
      const parsed = _.isDate(value) ? DateTime.fromJSDate(value) : DateTime.fromISO(value || "");
      return parsed.isValid
        ? new Date(parsed.toJSDate()).toLocaleDateString(company?.locale ?? "en-US")
        : "";
    },
    agGridSettings: {
      filter: "agDateColumnFilter",
      filterParams: DateFilterParams,
      cellClass: "date",
    },
  },
  OPTIONS: {},
  BOOLEAN: {},
  EQUITY: {
    formatter: ({ value }) => {
      if (!value) return "";

      return simpleFormatNumberEquity(value);
    },
  },
};

export const bonusTargetFieldsFormatter: FieldFormatterFunc<FieldProposal> = (params) => {
  const value = params.value;
  const proposal = params.instance;
  if (!_.isNumber(value)) {
    return value;
  }

  if (
    // @ts-expect-error TS2345
    getBonusTargetInputMode(proposal) === VariableInputMode.DOLLAR
  ) {
    // @ts-expect-error TS2722
    return typeMixins.DOLLAR.formatter(params);
  } else {
    // @ts-expect-error TS2722
    return typeMixins.PERCENT.formatter(params);
  }
};
